import { gql } from "lib/apolloClient";
import {
  questionFieldsFragments,
  assessmentToolFragments,
} from "Worksheet/modules/WorksheetFragments";

export const libraryFragments = {
  libraryEntityBasicDetails: gql`
    fragment libraryEntityBasicDetailsItem on LibraryGenericEntity {
      id
      type
      label
    }
  `,
  libraryEntityBasicSubjectDetailsItem: gql`
    fragment libraryEntityBasicSubjectDetailsItem on LibraryGenericEntity {
      id
      type
      label
      icon
      groupLabel
    }
  `,
};

export const entitySavedByNodeFragment = {
  unitPlan: gql`
    fragment unitSavedByNodeItem on UnitPlan {
      id
      savedBy {
        id
      }
    }
  `,
  assessment: gql`
    fragment assessmentSavedByNodeItem on Assessment {
      id
      savedBy {
        id
      }
      isSavedForLibrary
    }
  `,
  leCollection: gql`
    fragment leCollectionSavedByNodeItem on LECollection {
      id
      savedBy {
        id
      }
    }
  `,
  question: gql`
    fragment questionSavedByNodeItem on OrganizationResource {
      id
      isSavedForLibrary
    }
  `,
  questionV2: gql`
    fragment questionV2SavedByNodeItem on Question {
      id
      isSavedForLibrary
    }
  `,
  mockExam: gql`
    fragment mockExamSavedByNodeItem on MockExam {
      id
      isSavedForLibrary
    }
  `,
};

export const unitPlanFragment = {
  unitPlanFeed: gql`
    fragment unitPlanItem on UnitPlan {
      id
      owner {
        id
      }
      title {
        id
        value
      }
      image {
        id
        value
      }
      startDate
      endDate
      state
      templateId
      template {
        id
        name
      }
      unitType {
        id
        value
      }
      libraryGrades {
        ...libraryEntityBasicDetailsItem
      }
      librarySubjects {
        ...libraryEntityBasicDetailsItem
      }
      curriculumProgram {
        id
        type
        nodeInterfaceType
        label
        acronym
        academicSetupType
        __typename
      }
      showLibraryPremiumBannerForUser
      ...unitSavedByNodeItem
    }
    ${entitySavedByNodeFragment.unitPlan}
    ${libraryFragments.libraryEntityBasicDetails}
  `,
  unitPlanBannerFeed: gql`
    fragment unitPlanItem on UnitPlan {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
    }
  `,
};

export const assessmentFragment = {
  assessmentFeed: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      fields(
        uids: [
          "syllabusContentDP"
          "worksheetPaperType"
          "worksheetType"
          "subjectLevel"
          "relatedConceptsMYP"
          "learningObjectivesMyp"
          "conceptsMYP"
          "globalContextMYP"
          "toddleLibraryTopicsMYP"
        ]
      ) {
        id
        uid
        value
        resolvedMinimalTree {
          ... on ResolvedFieldGenericTags {
            id
            genericTags {
              id
              label
            }
          }
          ... on ResolvedFieldPlannerElementSet {
            id
            type
            nodes {
              id
              label
              depth
            }
          }
        }
      }
      image {
        id
        value
      }
      assessmentTools {
        edges {
          node {
            id
            assessmentToolItem {
              ... on ScoreAssessmentTool {
                id
                scoreToolData {
                  id
                  maxScore
                }
              }
            }
            assessmentToolType
          }
        }
      }
      organizationResourcesV2(
        first: null
        filters: { types: WORKSHEET_RESOURCE, resourceTypes: FOLDER }
      ) {
        resourceTypeBasedCount {
          totalCount
          questionStatusWiseCount {
            status
            count
          }
          questionTypeWiseCount {
            questionTypeUid
            count
          }
        }
      }
      libraryGrades {
        ...libraryEntityBasicDetailsItem
      }
      librarySubjects {
        ...libraryEntityBasicDetailsItem
      }
      ...assessmentSavedByNodeItem
      isSavedForLibrary
    }
    ${entitySavedByNodeFragment.assessment}
    ${libraryFragments.libraryEntityBasicDetails}
  `,
  assessmentFeedV2: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      fields(
        uids: [
          "syllabusContentDP"
          "worksheetPaperType"
          "worksheetType"
          "subjectLevel"
          "relatedConceptsMYP"
          "learningObjectivesMyp"
          "conceptsMYP"
          "globalContextMYP"
          "toddleLibraryTopicsMYP"
        ]
      ) {
        id
        uid
        value
        resolvedMinimalTree {
          ... on ResolvedFieldGenericTags {
            id
            genericTags {
              id
              label
            }
          }
          ... on ResolvedFieldPlannerElementSet {
            id
            type
            nodes {
              id
              label
              depth
            }
          }
        }
      }
      image {
        id
        value
      }
      assessmentTools {
        edges {
          node {
            id
            assessmentToolItem {
              ... on ScoreAssessmentTool {
                id
                scoreToolData {
                  id
                  maxScore
                }
              }
            }
            assessmentToolType
          }
        }
      }
      questions {
        totalCount
        questionTypeWiseCount {
          questionTypeUid
          count
        }
      }
      libraryGrades {
        ...libraryEntityBasicDetailsItem
      }
      librarySubjects {
        ...libraryEntityBasicDetailsItem
      }
      ...assessmentSavedByNodeItem
      isSavedForLibrary
    }
    ${entitySavedByNodeFragment.assessment}
    ${libraryFragments.libraryEntityBasicDetails}
  `,
  assessmentBannerFeed: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
    }
  `,
};

export const leCollectionFragment = {
  leCollectionFeed: gql`
    fragment leCollectionItem on LECollection {
      id
      name
      imageAttachment: image {
        id
        url
      }
      entities(entityType: ASSESSMENT, filters: $collectionFilterInput) {
        totalCount
      }
      libraryGrades {
        ...libraryEntityBasicDetailsItem
      }
      ...leCollectionSavedByNodeItem
    }
    ${entitySavedByNodeFragment.leCollection}
    ${libraryFragments.libraryEntityBasicDetails}
  `,
  leCollectionBannerFeed: gql`
    fragment leCollectionItem on LECollection {
      id
      name
      imageAttachment: image {
        id
        url
      }
    }
  `,
};

export const question = gql`
  fragment questionItem on Question {
    id
    difficulty
    questionType {
      id
      uid
      config
    }
    libraryUid
    questionScore
    updatedAt
    metadata
    presetQuestionId
    assessmentTools {
      totalCount
      edges {
        node {
          ...questionAssessmentToolItem
        }
      }
    }
    fields {
      ...questionFieldsItem
    }
    answerFields {
      ...questionFieldsItem
    }
    librarySubjects {
      id
      label
    }
    associatedEntities {
      id
      ... on Subject {
        id
        name
      }
      ... on GenericTag {
        id
        label
      }
      ... on PlannerElementNode {
        id
        label
        depth
      }
    }
  }
  ${questionFieldsFragments.questionFields}
  ${assessmentToolFragments.questionAssessmentTool}
`;

export const questionFeedItem = gql`
  fragment questionFeedItem on Question {
    id
    difficulty
    questionType {
      id
      uid
    }
    questionTitle {
      id
      uid
      value
    }
    questionScore
    librarySubjects {
      id
      label
    }
    associatedEntities {
      id
      ... on Subject {
        id
        name
      }
      ... on PlannerElementNode {
        id
        label
        depth
      }
    }
  }
`;

export const organizationResourceQuestionFragment = gql`
  fragment organizationResourceQuestionItem on OrganizationResource {
    id
    resourceType
    item {
      ...questionItem
    }
    isSavedForLibrary
    isLibraryPremium
    showLibraryPremiumBannerForUser(filters: $libraryBannerFilters)
    childrenV2(first: null, orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC) {
      resourceTypeBasedCount {
        totalCount
      }
      edges {
        node {
          id
          item {
            ...questionItem
          }
        }
      }
    }
  }
  ${question}
`;

export const organizationResourceQuestionFeedFragment = gql`
  fragment organizationResourceQuestionFeedItem on OrganizationResource {
    id
    resourceType
    item {
      ...questionFeedItem
    }
    isSavedForLibrary
    isLibraryPremium
    showLibraryPremiumBannerForUser
  }
  ${questionFeedItem}
`;

export const questionFeedItemV2 = gql`
  fragment questionFeedItemV2 on Question {
    id
    parentIdV2
    parentTypeV2
    difficulty
    questionType {
      id
      uid
    }
    questionTitle {
      id
      uid
      value
    }
    questionScore
    librarySubjects {
      id
      label
    }
    associatedEntities {
      id
      ... on Subject {
        id
        name
      }
      ... on GenericTag {
        id
        label
      }
      ... on PlannerElementNode {
        id
        label
        depth
      }
    }
  }
`;

export const questionDirectFeedFragmentV2 = gql`
  fragment questionDirectFeedItemV2 on Question {
    showLibraryPremiumBannerForUser
    isLibraryPremium
    isSavedForLibrary
    ...questionFeedItemV2
  }
  ${questionFeedItemV2}
`;

export const assessmentTitleFragment = gql`
  fragment assessmentTitle on Assessment {
    id
    title {
      id
      uid
      value
    }
  }
`;

export const associatedEntityTypesFragments = {
  plannerElementNode: gql`
    fragment plannerElementNodeItemV2 on PlannerElementNode {
      id
      label
      depth
      elementType: type
      associatedParents {
        id
        ... on Subject {
          id
          name
          isArchived
        }
      }
    }
  `,
  plannerElementNodeBasic: gql`
    fragment plannerElementNodeBasicItem on PlannerElementNode {
      id
      label
    }
  `,
};
